// Payroc PIN-pad (Ingenico) card-present flow, driven from the browser through the Node
// proxy. Faithful port of AmountToPayFragment: create a payment instruction (device prompts
// the customer) → poll the instruction status every ~3s until it leaves "inProgress" (2-min
// cap) → on "completed" read the payment details → approved iff transactionResult.responseCode
// === "A". Instruction status strings come from UtilsConstant.kt.

import {
  nodeCreateTerminalPayment,
  nodeTerminalPaymentStatus,
  nodeTerminalPaymentDetails,
  nodeReconcileTerminalPayment,
} from "./api";
import { addInFlight, removeInFlight } from "./inFlight";

// Wire card-type values (IngenicoCardType) + display labels + the amount_to_pay permission key.
export const CARD_TYPES = {
  CREDIT: "Credit",
  DEBIT: "Debit",
  FOOD_EBT: "FoodEbt",
  CASH_EBT: "CashEbt",
};

export const CARD_TYPE_META = {
  Credit: { label: "Credit Card", permission: "MENU_CREDIT_DEBIT_PAYMENT" },
  Debit: { label: "Debit Card", permission: "MENU_CREDIT_DEBIT_PAYMENT" },
  FoodEbt: { label: "EBT Food", permission: "MENU_FOOD_EBT" },
  CashEbt: { label: "EBT Cash", permission: "MENU_CASH_EBT" },
};

// Terminal instruction status. Poll while inProgress; terminal states below. Payroc's docs say
// "failed" but the Android app + gateway also emit "failure" — handle both.
const INPROGRESS = "inprogress";
const COMPLETED = "completed";
const CANCELED = "canceled";
const FAILURE = "failure";
const FAILED = "failed";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const isOk = (r) => r && (r.status === true || r.status === "true");
const norm = (s) => String(s == null ? "" : s).toLowerCase();

/**
 * Run the full terminal payment. Resolves to a normalized result — it never throws.
 *   { approved:true, paymentId, cardBrand, cvvResult, approvalCode, authorizedAmount, tipAmount, responseMessage, raw }
 *   { approved:false, canceled?, declined?, timedOut?, reason, raw? }
 *
 * @param {Object} p
 * @param {string} p.orderId    client order id (also becomes Payroc order.orderId)
 * @param {number} p.amount     dollars to charge
 * @param {string} p.cardType   one of CARD_TYPES
 * @param {string} [p.operator] cashier name
 * @param {(stage:string, info?:Object)=>void} [p.onStage]  "creating" | "waiting" | "processing"
 * @param {()=>boolean} [p.shouldCancel]  cooperative cancel between polls
 * @param {number} [p.pollMs]   default 3000
 * @param {number} [p.timeoutMs] default 120000 (2 min, matches Android)
 */
export async function runTerminalPayment({
  orderId,
  amount,
  cardType,
  operator = "",
  idempotencyKey,
  onStage,
  shouldCancel,
  pollMs = 3000,
  timeoutMs = 180000, // 3 min — a single Payroc status GET long-polls up to 60s
} = {}) {
  const stage = (s, info) => {
    if (typeof onStage === "function") onStage(s, info);
  };

  // Missing-transaction guard: ask Payroc "did a payment for this orderId already succeed?" — used
  // when we lose the status/details (poll timed out, network dropped). Returns an approved result to
  // recover, or null.
  const reconcile = async () => {
    try {
      const r = await nodeReconcileTerminalPayment({ orderId });
      if (isOk(r) && r.found && r.approved) {
        removeInFlight(orderId);
        return {
          approved: true,
          reconciled: true,
          paymentId: r.payment_id,
          cardBrand: r.card_type || "",
          cardLast4: r.card_last4 || "",
          cvvResult: r.cvv_result || "",
          approvalCode: r.approval_code || "",
          authorizedAmount: r.authorized_amount,
          tipAmount: r.tip_amount,
          responseMessage: r.response_message || "Approved (recovered)",
          raw: r.raw,
        };
      }
    } catch {
      /* reconcile is best-effort */
    }
    return null;
  };

  stage("creating");
  // Create with retry: a network error on create may still have created the instruction (and charged
  // the card). Retry with the SAME idempotency key — Payroc dedups (returns the original response),
  // so this can never double-charge (verified in the idempotency docs). A clean error response is
  // NOT retried (it's definitive). If create never clearly succeeds, reconcile before giving up.
  let created = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      created = await nodeCreateTerminalPayment({ orderId, amount, cardType, operator, idempotencyKey });
      break;
    } catch (e) {
      created = { status: false, reason: friendly(e) };
      if (attempt < 2) await sleep(800);
    }
  }
  if (!isOk(created) || !created.payment_instruction_id) {
    const rec = await reconcile(); // a prior (same-key) attempt may have charged — verify
    if (rec) return rec;
    return { approved: false, reason: created?.reason || created?.message || "Failed to start payment", raw: created };
  }

  const instructionId = created.payment_instruction_id;
  // Track the in-flight charge so a browser crash mid-payment can be reconciled on the next load.
  addInFlight({ terminalOrderId: orderId, cardType, amount, idempotencyKey });
  stage("waiting", { instructionId });

  const start = Date.now();
  let paymentId = null;
  while (Date.now() - start < timeoutMs) {
    if (typeof shouldCancel === "function" && shouldCancel()) {
      return { approved: false, canceled: true, reason: "Canceled" };
    }
    await sleep(pollMs);
    if (typeof shouldCancel === "function" && shouldCancel()) {
      return { approved: false, canceled: true, reason: "Canceled" };
    }

    let st;
    try {
      st = await nodeTerminalPaymentStatus({ paymentInstructionId: instructionId });
    } catch {
      continue; // transient network → retry (mirrors Android UnknownHost/SocketTimeout retry)
    }
    if (!isOk(st)) {
      continue; // transient Node/Payroc error → keep polling (bounded by timeout, then reconcile)
    }
    const s = norm(st.instruction_status);
    if (s === COMPLETED) {
      paymentId = st.payment_id;
      break;
    }
    if (s === CANCELED) {
      removeInFlight(orderId); // Payroc canceled the instruction → no charge
      return { approved: false, canceled: true, reason: "Transaction canceled", raw: st };
    }
    if (s === FAILURE || s === FAILED) {
      // "failed" should mean no charge, but verify anyway before reporting failure (max safety).
      const rec = await reconcile();
      if (rec) return rec;
      removeInFlight(orderId);
      return { approved: false, reason: st.error_message || "Transaction failed", raw: st };
    }
    // INPROGRESS (or unknown) → keep polling
    void INPROGRESS;
  }

  if (!paymentId) {
    // Timed out without a completed status — but the PIN pad may have finished after our last poll.
    const rec = await reconcile();
    if (rec) return rec;
    return { approved: false, timedOut: true, reason: "The transaction has timed out" };
  }

  stage("processing", { paymentId });
  let details;
  try {
    details = await nodeTerminalPaymentDetails({ paymentId });
  } catch (e) {
    const rec = await reconcile();
    if (rec) return rec;
    return { approved: false, reason: friendly(e), raw: null };
  }
  if (!isOk(details)) {
    const rec = await reconcile();
    if (rec) return rec;
    return { approved: false, reason: details?.reason || details?.message || "Could not read payment", raw: details };
  }

  if (details.approved) {
    removeInFlight(orderId);
    return {
      approved: true,
      paymentId: details.payment_id || paymentId,
      cardBrand: details.card_type || "",
      cardLast4: details.card_last4 || "",
      cvvResult: details.cvv_result || "",
      approvalCode: details.approval_code || "",
      authorizedAmount: details.authorized_amount, // cents string
      tipAmount: details.tip_amount, // cents string
      responseMessage: details.response_message || "Approved",
      raw: details.raw,
    };
  }
  removeInFlight(orderId); // Payroc returned a definitive declined/failed result → no open charge
  return {
    approved: false,
    declined: !!details.declined,
    reason: details.declined
      ? "Transaction Declined!"
      : "Transaction Declined or Failed! Please Try Again.",
    raw: details.raw,
  };
}

function friendly(e) {
  const m = e && e.message ? e.message : String(e || "");
  if (/network|timeout|ECONN|ERR_/i.test(m)) return "Network error — could not reach the payment service";
  return m || "Payment error";
}
